import React, { useState, useEffect } from "react";
import { getSalaryRegister } from "../../api/salaryApi";
import { getAllAllowance } from "../../api/allowanceApi";
import { getAllDeduction } from "../../api/deductionApi";

export default function HeadwiseSalaryReport({ reportName, month: parentMonth, year: parentYear }) {
  const [salaryData, setSalaryData] = useState([]);
  const [allowancesMaster, setAllowancesMaster] = useState([]);
  const [deductionsMaster, setDeductionsMaster] = useState([]);
  const [selectedHead, setSelectedHead] = useState(""); 
  const [errorMessage, setErrorMessage] = useState("");

  const [company, setCompany] = useState(sessionStorage.getItem("companyName"));
  const [email, setEmail] = useState(sessionStorage.getItem("email"));
  const [address, setAddress] = useState(sessionStorage.getItem("address"));
  
  // Fetch master data once
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const allowance = await getAllAllowance();
        const deduction = await getAllDeduction();
        setAllowancesMaster(allowance);
        setDeductionsMaster(deduction);
      } catch (error) {
        console.error("Error fetching master data:", error);
        setErrorMessage("Failed to fetch master data");
      }
    };
    fetchMasterData();
  }, []);

  // Fetch salary data automatically
  useEffect(() => {
    const fetchSalary = async () => {
      if (!reportName || !parentMonth || !parentYear) return;

      const filters = {
        month: parseInt(parentMonth),
        year: parseInt(parentYear),
        departmentId: null,
        sectionId: null,
      };

      try {
        const data = await getSalaryRegister(filters);
        setSalaryData(data);
        setErrorMessage(""); 
      } catch (error) {
        setSalaryData([]);
        setErrorMessage(error.message || "Failed to get salary register details");
      }
    };

    fetchSalary();
  }, [reportName, parentMonth, parentYear]);

  // Create head options
  const allHeads = [
    ...allowancesMaster.map((a) => ({
      type: "Allowance",
      name: a.allowanceName,
      field: `allowance${a.allowanceID}`,
    })),
    ...deductionsMaster.map((d) => ({
      type: "Deduction",
      name: d.deductionName,
      field: `deduction${d.deductionID}`,
    })),
  ];

  // Filter data for selected head
  const filteredData = selectedHead
    ? salaryData.map((emp, idx) => ({
        index: idx + 1,
        id: emp.srNo,
        name: emp.employeeName,
        code: emp.employeeCode,
        amount: emp[selectedHead] || 0,
      }))
    : [];

  // Calculate total amount
  const totalAmount = filteredData.reduce((sum, emp) => sum + emp.amount, 0);

  // Print handler
  const handlePrint = () => {
    if (!selectedHead || filteredData.length === 0) {
      alert("No data to print");
      return;
    }

    const printContent = `
      <html>
        <head>
          <title>Headwise Salary Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #000; padding: 5px; text-align: left; }
            th { background-color: #f0f0f0; text-align: center; }
            .text-end { text-align: right; }
            .fw-bold { font-weight: bold; }
             .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .header strong {
              display: block;
              font-size: 14px;
              margin: 2px 0;
            }
            .center {
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <strong>${company}</strong>
            <strong>Email: ${email}</strong>
            <strong>Address: ${address}</strong>
          </div>
          <h5 style="text-align: center;">Headwise Salary Report - ${parentMonth}/${parentYear}</h5>
          <p>Head: ${allHeads.find(h => h.field === selectedHead)?.type} - ${allHeads.find(h => h.field === selectedHead)?.name}</p>
          <table>
            <thead>
              <tr>
                <th>Sr.No.</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(emp => `
                <tr>
                  <td class="center">${emp.index}</td>
                  <td>${emp.code}</td>
                  <td>${emp.name}</td>
                  <td class="text-end">${emp.amount}</td>
                </tr>
              `).join('')}
              <tr>
                <td colspan="3" class="text-end fw-bold">Total</td>
                <td class="text-end fw-bold">${totalAmount}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div>
      <h5>Headwise Salary Report - {parentMonth}/{parentYear}</h5>

      {/* Head Dropdown */}
      <div className="mb-3 col-4">
        <label>Select Allowance/Deduction</label>
        <select
          value={selectedHead}
          onChange={(e) => setSelectedHead(e.target.value)}
          className="form-select form-select-sm"
        >
          <option value="">-- Select Head --</option>
          <optgroup label="Allowances">
            {allowancesMaster.map((a) => (
              <option key={a.allowanceID} value={`allowance${a.allowanceID}`}>
                Allowance{a.allowanceID} - {a.allowanceName}
                {/* {a.allowanceName} */}
              </option>
            ))}
          </optgroup>
          <optgroup label="Deductions">
            {deductionsMaster.map((d) => (
              <option key={d.deductionID} value={`deduction${d.deductionID}`}>
                Deduction{d.deductionID} - {d.deductionName}
                {/* {d.deductionName} */}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {errorMessage && <p className="text-danger">{errorMessage}</p>}

      {selectedHead && filteredData.length > 0 && (
        <>
          <button className="btn btn-primary mb-2" onClick={handlePrint}>
            Print Report
          </button>
          <table className="table table-bordered table-sm mt-2" 
                // style={{ borderRadius: "10px" , overflow: "hidden" }}
          >
            <thead>
              <tr>
                <th className="text-center">Sr.No.</th>
                <th className="text-center">Employee Code</th>
                <th className="text-center">Employee Name</th>
                <th className="text-center">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((emp) => (
                <tr key={emp.id}>
                  <td className="text-center">{emp.index}</td>
                  <td>{emp.code}</td>
                  <td>{emp.name}</td>
                  <td className="text-end">{emp.amount}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="text-end fw-bold">Total</td>
                <td className="text-end fw-bold">{totalAmount}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {selectedHead && filteredData.length === 0 && <p>No data available for selected head.</p>}
    </div>
  );
}

